<script setup lang="ts" generic="T">
import { computed, ref, watch } from 'vue'

import CarouselButton from './CarouselButton.vue'
import CarouselItem from './CarouselItem.vue'
import CarouselViewport from './CarouselViewport.vue'

type Props = {
  items: Array<T>
  initialIndex?: number
  shouldShowNavigation?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  initialIndex: 0,
  shouldShowNavigation: true,
})

// state
const itemWidths = ref<number[]>([])
const carouselWidth = ref(0)
const currentIndex = ref(0)
const isMounted = ref(false)

// computed
const shouldShowPrevButton = computed(() => {
  return (
    props.shouldShowNavigation &&
    currentIndex.value > 0 &&
    translateShift.value > 0
  )
})

const shouldShowNextButton = computed(() => {
  return (
    props.shouldShowNavigation &&
    currentIndex.value + itemsPerPage.value < props.items.length
  )
})

const gap = computed(() => {
  const fontSize = parseFloat(document.documentElement.style.fontSize || '16px')
  return 0.375 * fontSize
})

const totalWidth = computed(() => {
  return itemWidths.value.reduce((acc, width) => acc + width + gap.value, 0)
})

const itemsPerPage = computed(() => {
  const carouselWidthValue = carouselWidth.value
  const totalWidthValue = totalWidth.value
  const itemsLength = props.items.length

  if (!carouselWidthValue || !totalWidthValue) {
    return itemsLength
  }
  const averageItemWidth = totalWidthValue / itemsLength
  const calculatedItemsPerPage = Math.floor(
    carouselWidthValue / averageItemWidth,
  )

  return calculatedItemsPerPage >= itemsLength
    ? itemsLength - 1
    : calculatedItemsPerPage
})

const translateShift = computed(() => {
  if (itemsPerPage.value + currentIndex.value >= props.items.length) {
    const indexToLook =
      currentIndex.value - 1 === 0 ? 1 : currentIndex.value - 1
    const leftSideShift =
      totalWidth.value - carouselWidth.value + gap.value * currentIndex.value
    const rightSideShift = itemWidths.value
      .slice()
      .reverse()
      .slice(0, indexToLook)
      .reduce((acc, width) => acc + width, 0)
    return leftSideShift > rightSideShift ? leftSideShift : rightSideShift
  }
  return itemWidths.value
    .slice(0, currentIndex.value)
    .reduce((acc, width) => acc + width + gap.value, 0)
})

// methods
function next(): void {
  currentIndex.value = Math.min(
    currentIndex.value + 1,
    props.items.length - itemsPerPage.value,
  )
}

function prev(): void {
  currentIndex.value = Math.max(currentIndex.value - 1, 0)
}

function changeItemWidth(width: number, itemIndex: number): void {
  const previousWidth = itemWidths.value[itemIndex]
  if (previousWidth !== width) {
    itemWidths.value[itemIndex] = width
  }
}

function setCarouselViewportWidth(width: number): void {
  carouselWidth.value = width
}

function setInitialIndex(
  newItemsPerPage: number,
  newItemsLength: number,
): void {
  currentIndex.value =
    props.initialIndex + newItemsPerPage >= newItemsLength - 1
      ? newItemsLength - newItemsPerPage
      : props.initialIndex
}

function updateCurrentIndex(
  newItemsPerPage: number,
  newItemsLength: number,
): void {
  currentIndex.value =
    currentIndex.value + newItemsPerPage >= newItemsLength - 1
      ? newItemsLength - newItemsPerPage
      : currentIndex.value
}

// init
watch(
  [itemsPerPage, () => props.items],
  ([newItemsPerPage, newItems]) => {
    if (itemWidths.value.length > newItems.length) {
      itemWidths.value = itemWidths.value.slice(0, newItems.length)
    }
    if (totalWidth.value === 0) {
      return
    }
    if (!isMounted.value) {
      setInitialIndex(newItemsPerPage, newItems.length)
      isMounted.value = true
    } else {
      updateCurrentIndex(newItemsPerPage, newItems.length)
    }
  },
  { immediate: true },
)
</script>

<template>
  <div
    class="flex w-full gap-1"
    :class="{ 'justify-center': !shouldShowNavigation }"
  >
    <CarouselButton
      v-if="shouldShowNavigation"
      ariaLabel="carousel-previous-button"
      iconName="chevron-left"
      :showButton="shouldShowPrevButton"
      :onClick="prev"
    />
    <CarouselViewport
      aria-label="carousel-viewport"
      :translateShift
      :shouldShowNavigation
      @carouselViewportWidth="setCarouselViewportWidth"
    >
      <CarouselItem
        v-for="(item, index) in items"
        :key="index"
        @itemWidth="(width) => changeItemWidth(width, index)"
      >
        <slot
          name="item"
          :item
        />
      </CarouselItem>
    </CarouselViewport>
    <CarouselButton
      v-if="shouldShowNavigation"
      ariaLabel="carousel-next-button"
      iconName="chevron-right"
      :showButton="shouldShowNextButton"
      :onClick="next"
    />
  </div>
</template>
